import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BEtrTradeProvider } from '../../common/providers/b-etr-trade.provider';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { numberValueFormatter } from '../../../shared/helpers/number-value-formatter';
import { OpenTradeBaseComponent } from '../open-trade-base.component';

@Component({
  standalone: true,
  selector: 'app-b-etr-trade',
  templateUrl: './b-etr-trade.component.html',
  styleUrls: ['./b-etr-trade.component.scss'],
  imports: [
    CommonModule,
    NgxSpinnerModule,
    ReactiveFormsModule,
    SharedModule,
    FormsModule,
    TooltipModule,
    DatePickerComponent,
  ],
  providers: [BEtrTradeProvider],
})
export class BEtrTradeComponent extends OpenTradeBaseComponent {
  readonly #bEtrTradeProvider = inject(BEtrTradeProvider);

  bEtrRSATradeForm: FormGroup;
  bEtrTradeForm: FormGroup;
  hasCreditor: string;

  protected override onInitialize(): void {
    this.hasCreditor = this.currentData?.creditorId;

    if (this.isEditTrade) {
      this.componentService.pageTitle.set('Edit a Trade');
      if (this.currentData.type === 'RSA') {
        this.detectExistRsa = false;
        this.isShowRsaForm = true;
        this.#bEtrTradeProvider.initRSAForm({
          ...this.currentData,
          creditorId: this.hasCreditor,
          type: 'RSA',
        });
        this.bEtrRSATradeForm = this.#bEtrTradeProvider.bEtrRSATradeForm;
      } else {
        this.detectExistRsa = false;
        this.isShowRsaForm = false;
        this.initBETRTradeForm(
          {
            ...this.currentData,
            faceValue:
              this.currentData.leftFaceValue || this.currentData.faceValue,
            rsaTradeId: this.currentData.tradeId,
          },
          true,
        );
      }
    }
  }

  protected override getMainForm(): FormGroup {
    return this.isShowRsaForm ? this.bEtrRSATradeForm : this.bEtrTradeForm;
  }

  protected override onCreateSuccess(response: any, data: any): void {
    if (response.type === 'RSA') {
      this.initBETRTradeForm(response);
    } else {
      this.closeModal();
    }
  }

  onSubmit(): void {
    if (this.bEtrRSATradeForm.invalid) return;
    const rawData = this.bEtrRSATradeForm.getRawValue();
    const formattedRawData = { ...rawData };
    formattedRawData.type = 'RSA';
    formattedRawData.debtor = rawData.debtor ? rawData.debtor : '';
    this.submitFormData(formattedRawData);
  }

  onSubmitETRTrade(): void {
    if (this.bEtrTradeForm.invalid) return;
    const rawData = this.bEtrTradeForm.getRawValue();
    const formattedRawData = { ...rawData };
    formattedRawData.type = this.type || '';
    formattedRawData.edso = rawData.edso ? Number(rawData.edso) : null;
    formattedRawData.faceValue = rawData.faceValue
      ? Number(rawData.faceValue)
      : null;
    formattedRawData.deposit = rawData.deposit ? +rawData.deposit / 100 : null;
    delete formattedRawData.description;
    delete formattedRawData.pod;
    delete formattedRawData.rejectedMessage;

    this.spinnerService.setShowSpinner(true);
    this.currentData?.acquisitionId
      ? this.doUpdate(formattedRawData)
      : this.doCreate(formattedRawData);
  }

  selectCreditorForDetectRSA(event: any): void {
    if (!(event && event.id)) {
      this.detectExistRsa = true;
      return;
    }

    this.detectRSAForCreditor(
      event.id,
      (data) => this.initBETRTradeForm(data),
      () => {
        this.#bEtrTradeProvider.initRSAForm({
          creditorId: event.id,
          type: 'RSA',
        });
        this.bEtrRSATradeForm = this.#bEtrTradeProvider.bEtrRSATradeForm;
      },
    );
  }

  override fileChange(element: Event, acronym: string): void {
    super.fileChange(element, acronym, this.bEtrTradeForm);
  }

  get form2TradeDocumentsControl() {
    return this.getTradeDocumentsControl(this.bEtrTradeForm);
  }

  private initBETRTradeForm(data: any, isEdit?: boolean): void {
    this.bEtrRSATradeForm?.reset();
    this.isShowRsaForm = false;
    this.#bEtrTradeProvider.initTradeForm(data, isEdit);
    this.bEtrTradeForm = this.#bEtrTradeProvider.bEtrTradeForm;

    const selectedCredebtor =
      this.creditorList.find((creditor) => creditor.id === data.creditorId) ||
      this.debtorList.find((debtor) => debtor.id === data.debtorId);

    if (selectedCredebtor) {
      this.showTradeDocumentForEdit(
        selectedCredebtor,
        this.bEtrTradeForm,
        this.currentData?.documents,
      );
    }

    this.totalFaceValue = data.faceValue;
    this.remainingValue = numberValueFormatter({ value: data.faceValue });
    this.bEtrTradeForm.get('faceValue')?.valueChanges.subscribe((value) => {
      this.calculateRemainingValue(value);
    });

    this.subscribeToInvoiceDateChange(this.bEtrTradeForm);
    this.subscribeToPaymentTermsChange(this.bEtrTradeForm);
    this.subscribeToExpectedDateChange(this.bEtrTradeForm);
  }
}
